import React, { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Camera, CameraOff, KeyboardIcon, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * OwnerQrScanner
 * Attiva la fotocamera del dispositivo (facingMode 'environment') e scansiona
 * il QR dell'ospite. Prova nell'ordine:
 *   1) window.BarcodeDetector nativo (Chrome/Android, Edge)
 *   2) jsQR su canvas (funziona ovunque: iOS Safari, Firefox, ecc.)
 *   3) fallback: incolla manuale dell'ID
 * onCheckedIn(booking_id): callback per aggiornare la lista giornaliera.
 */
export default function OwnerQrScanner({ open, onOpenChange, onCheckedIn }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detectorRef = useRef(null);
  const scanningRef = useRef(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualId, setManualId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [engine, setEngine] = useState("");

  useEffect(() => {
    if (!open) return;
    setResult(null); setError(null); setManualId(""); setManualMode(false); setCameraReady(false);
    const hasNative = typeof window !== "undefined" && "BarcodeDetector" in window;
    if (hasNative) {
      try {
        detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
        setEngine("native");
      } catch { setEngine("jsqr"); }
    } else {
      setEngine("jsqr");
    }
    startCamera();
    return stopCamera;
    // eslint-disable-next-line
  }, [open]);

  const startCamera = async () => {
    setError(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Il tuo browser non consente l'accesso alla fotocamera. Usa la modalità manuale.");
      setManualMode(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        await videoRef.current.play();
      }
      setCameraReady(true);
      scanningRef.current = true;
      scanLoop();
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Accesso alla fotocamera negato. Consenti l'accesso nelle impostazioni del browser o inserisci l'ID manualmente."
        : "Fotocamera non disponibile su questo dispositivo. Usa la modalità manuale.";
      setError(msg);
      setManualMode(true);
    }
  };

  const stopCamera = () => {
    scanningRef.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  };

  const scanLoop = async () => {
    if (!scanningRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(scanLoop);
      return;
    }
    try {
      let payload = null;
      if (detectorRef.current) {
        try {
          const codes = await detectorRef.current.detect(video);
          if (codes && codes.length > 0) payload = codes[0].rawValue;
        } catch { /* fall through */ }
      }
      if (!payload) {
        const canvas = canvasRef.current || (canvasRef.current = document.createElement("canvas"));
        const w = video.videoWidth, h = video.videoHeight;
        if (w > 0 && h > 0) {
          const scale = Math.min(1, 640 / w);
          canvas.width = Math.floor(w * scale);
          canvas.height = Math.floor(h * scale);
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
          if (code && code.data) payload = code.data;
        }
      }
      if (payload) {
        stopCamera();
        await submit({ qr_payload: payload });
        return;
      }
    } catch { /* transient */ }
    rafRef.current = requestAnimationFrame(scanLoop);
  };

  const submit = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/checkin", body);
      setResult(data);
      if (data.already_checked_in) toast.info("Ospite già registrato in precedenza");
      else toast.success("Ingresso registrato");
      onCheckedIn?.(data.booking_id);
    } catch (e) {
      setError(e.response?.data?.detail || "QR non valido");
    } finally { setBusy(false); }
  };

  const submitManual = (e) => {
    e.preventDefault();
    const bid = manualId.trim();
    if (!bid) return;
    stopCamera();
    submit(bid.startsWith("POOLPASS|") ? { qr_payload: bid } : { booking_id: bid });
  };

  const rescan = () => {
    setResult(null); setError(null); setManualId("");
    if (!manualMode) startCamera();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopCamera(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#005C7A]" /> Scansiona QR ospite
          </DialogTitle>
          <DialogDescription>
            Inquadra il QR code stampato o mostrato dal cliente per convalidare la prenotazione.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className={`rounded-2xl p-5 ${result.already_checked_in ? "bg-amber-50 border-2 border-amber-300" : "bg-green-50 border-2 border-green-300"}`} data-testid="qr-result">
            <div className="flex items-center gap-2 mb-3">
              {result.already_checked_in ? (
                <><CheckCircle2 className="w-6 h-6 text-amber-600" /><span className="font-display text-lg font-bold text-amber-900">Ospite già entrato</span></>
              ) : (
                <><CheckCircle2 className="w-6 h-6 text-green-600" /><span className="font-display text-lg font-bold text-green-900">Prenotazione Valida</span></>
              )}
            </div>
            {result.already_checked_in ? (
              <div className="text-sm mb-3 text-amber-900" data-testid="already-note">
                Ingresso già registrato in precedenza. <strong>Non incassare di nuovo.</strong> Verifica lo stato in dashboard prima di procedere.
              </div>
            ) : (
              <div className="text-sm mb-3">
                Incassare <strong className="text-[#005C7A] text-lg">€{result.amount?.toFixed(2)}</strong> tramite <strong>{result.payment_method_label}</strong> dal cliente
              </div>
            )}
            <div className="bg-white/70 border border-border rounded-lg p-3 text-xs space-y-1">
              <div><span className="text-muted-foreground">Ospite:</span> <strong>{result.guest_name || "—"}</strong></div>
              <div><span className="text-muted-foreground">Piscina:</span> {result.pool_title}</div>
              <div><span className="text-muted-foreground">Data:</span> {result.date} · <span className="text-muted-foreground">Slot:</span> {result.slot === "half" ? "Mezza giornata" : "Giornata intera"}</div>
              <div><span className="text-muted-foreground">Ospiti:</span> {result.guests}</div>
              <div className="font-mono pt-1 text-[10px]">{result.booking_id}</div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={rescan} variant="outline" className="rounded-full flex-1" data-testid="qr-rescan">Scansiona un altro</Button>
              <Button onClick={() => { stopCamera(); onOpenChange(false); }} className="rounded-full bg-[#005C7A] flex-1" data-testid="qr-done">Chiudi</Button>
            </div>
          </div>
        ) : manualMode ? (
          <form onSubmit={submitManual} className="space-y-3">
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r p-3 text-xs text-amber-900 flex items-start gap-2">
              <CameraOff className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>{error || "Modalità manuale — incolla o digita l'ID della prenotazione (o l'intero payload QR)."}</div>
            </div>
            <Input
              autoFocus
              placeholder="bk_xxxxxxxx o POOLPASS|bk_xxxx|…"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              data-testid="qr-manual-input"
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={busy || !manualId.trim()} className="rounded-full bg-[#005C7A] flex-1" data-testid="qr-manual-submit">
                {busy ? "…" : "Convalida"}
              </Button>
              <Button type="button" onClick={() => { setManualMode(false); setError(null); startCamera(); }} variant="outline" className="rounded-full">
                <Camera className="w-4 h-4 mr-1" /> Fotocamera
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="relative aspect-square bg-black rounded-2xl overflow-hidden">
              <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" data-testid="qr-video" />
              <div className="absolute inset-8 border-2 border-white/60 rounded-2xl pointer-events-none animate-pulse" />
              {cameraReady && (
                <div className="absolute bottom-3 left-3 right-3 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full text-xs text-white text-center flex items-center justify-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Inquadra il QR… {engine && <span className="opacity-60">({engine === "native" ? "veloce" : "compatibile"})</span>}
                </div>
              )}
              {!cameraReady && !error && (
                <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm">
                  <Camera className="w-4 h-4 mr-2" /> Avvio fotocamera…
                </div>
              )}
            </div>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-r p-3 text-xs text-red-900 flex items-start gap-2">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
              </div>
            )}
            <button
              onClick={() => { stopCamera(); setManualMode(true); }}
              className="w-full text-sm text-[#005C7A] hover:underline flex items-center justify-center gap-1"
              data-testid="qr-manual-toggle"
            >
              <KeyboardIcon className="w-3.5 h-3.5" /> Inserisci ID manualmente
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
